import type { JSX, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Empty, Skeleton } from 'antd';
import {
  AlertOutlined,
  AuditOutlined,
  BankOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  FileAddOutlined,
  FileTextOutlined,
  FireOutlined,
  InboxOutlined,
  LockOutlined,
  NodeIndexOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReadOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SoundOutlined,
  StarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

import { authApi, type WikiMeDto } from '../lib/authApi';
import {
  educationApi,
  lmsAdminApi,
  newsApi,
  openingApi,
  standardsApi,
  wikiAdminApi,
  wikiApi,
  type CourseListItemDto,
  type MyPathDto,
  type NewsItemDto,
  type ReviewQueueItem,
} from '../lib/wikiApi';
import { hubApi, type HubCountsDto } from '../lib/api/hub';
import { learnApi, type LearnCourseListItem } from '../lib/api/learn';
import { audienceOf, type Audience } from '../lib/nav';
import { readRecent } from '../lib/recent';
import { WEEKDAY_DATE, firstNameOf, greetingOf, levelOf, pathDayLine, pickContinueCourse, plural, summaryLine } from '../lib/hub-home';
import { Button } from '../components/ds';
import { CoverArt, CoverHeroText, coverVisual } from '../components/learn/CourseCover';
import { NewArticleModal } from '../components/NewArticleModal';
import { NewsModal } from './NewsPage';
import { QuestionOfDay } from '../components/QuestionOfDay';
import { PointsWidget } from '../components/PointsWidget';
import { OnboardingHero, isOnboardingDone } from '../components/OnboardingHero';
import './HomePage.css';

/**
 * Главная FISHKA HUB 2.0 (PageHome, план wk6 §1.2) — по роли из /wiki/auth/me:
 *   УК       — обучение сети (KPI + роли), очередь ответов учеников, что сделать;
 *   партнёр  — обучение моих точек (GET /wiki/hub/my-points-learning), открытие;
 *   управл.  — команда точки (наставник), рейтинг команды;
 *   сотрудник — «Продолжить обучение», моя траектория, вопрос дня.
 * Подстрока приветствия — GET /wiki/hub/summary (в скоупе вьюера), плитки —
 * общие счётчики GET /wiki/hub/counts (тот же запрос, что бейджи меню).
 * KPI сети без дельт: learning-kpis (W5) ещё нет — берём analyticsOverview.
 */

// ── Мелкие помощники ────────────────────────────────────────────────

const pl = (n: number, forms: [string, string, string]): string => `${n.toLocaleString('ru-RU')} ${plural(n, forms)}`;

function relTime(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  const h = Math.round(diffMin / 60);
  if (h < 24 && d.toDateString() === new Date().toDateString()) return `${h} ч назад`;
  const y = new Date();
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

const fmtDay = (iso: string): string => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('') || '—';

const AV_COLORS = ['var(--fk-chart-1)', 'var(--fk-chart-2)', 'var(--fk-chart-3)', 'var(--fk-chart-5)', 'var(--fk-chart-6)', 'var(--fk-chart-4)'];
const avColor = (key: string): string => AV_COLORS[[...key].reduce((s, c) => s + c.charCodeAt(0), 0) % AV_COLORS.length];

type Tone = 'orange' | 'red' | 'blue' | 'purple' | 'green';
const TONE: Record<Tone, [string, string]> = {
  orange: ['var(--fk-orange-soft)', 'var(--fk-orange)'],
  red: ['var(--fk-error-soft)', 'var(--fk-error)'],
  blue: ['var(--fk-info-soft)', 'var(--fk-info)'],
  purple: ['rgba(156, 91, 212, .12)', 'var(--fk-chart-5)'],
  green: ['var(--fk-success-soft)', 'var(--fk-success-text)'],
};

function Card({ icon, title, note, count, link, children, className }: { icon?: ReactNode; title: string; note?: string; count?: number | null; link?: { to: string; label: string } | null; children: ReactNode; className?: string }): JSX.Element {
  return (
    <section className={`fk-hub-card${className ? ` ${className}` : ''}`}>
      <div className="fk-hub-card-head">
        <div className="fk-hub-card-title">
          {icon}
          <h2>{title}</h2>
          {note && <span className="fk-hub-card-note">· {note}</span>}
          {count ? <span className="fk-hub-count">{count}</span> : null}
        </div>
        {link && (
          <Link to={link.to} className="fk-hub-card-link">
            {link.label} →
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function Bar({ pct, color = 'var(--fk-orange)' }: { pct: number; color?: string }): JSX.Element {
  return (
    <div className="fk-hub-bar">
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} />
    </div>
  );
}

interface StatDef {
  key: string;
  value: ReactNode;
  label: string;
  icon: ReactNode;
  tone: Tone;
  to?: string;
}

function HomeStat({ s }: { s: StatDef }): JSX.Element {
  const [bg, fg] = TONE[s.tone];
  const inner = (
    <>
      <span className="fk-hub-row-ic" style={{ width: 40, height: 40, background: bg, color: fg, fontSize: 19 }}>{s.icon}</span>
      <span style={{ lineHeight: 1.15, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 24, fontWeight: 800, color: 'var(--fk-text)', fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 2 }}>{s.label}</span>
      </span>
    </>
  );
  const style = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', textDecoration: 'none', color: 'inherit' } as const;
  return s.to ? (
    <Link to={s.to} className="fk-hub-card fk-tile" style={style}>
      {inner}
    </Link>
  ) : (
    <div className="fk-hub-card" style={style}>
      {inner}
    </div>
  );
}

// ── Страница ────────────────────────────────────────────────────────

export function HomePage(): JSX.Element {
  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const me = meQuery.data;
  const audience = audienceOf(me);
  const isLearner = audience === 'employee' || audience === 'manager';

  const summaryQ = useQuery({ queryKey: ['wiki', 'hub', 'summary'], queryFn: () => hubApi.summary(), enabled: !!me, staleTime: 5 * 60_000, retry: false });
  const countsQ = useQuery({ queryKey: ['wiki', 'hub', 'counts'], queryFn: () => hubApi.counts(), enabled: !!me, refetchInterval: 60_000, retry: false });
  // W3: список курсов с continueLesson* (аддитивно) — тот же эндпоинт и ключ, что educationApi.listCourses.
  const coursesQ = useQuery({ queryKey: ['wiki', 'education', 'courses'], queryFn: () => learnApi.listCourses(), enabled: !!me, retry: false });
  const standardsQ = useQuery({ queryKey: ['wiki', 'standards'], queryFn: () => standardsApi.get(), enabled: !!me, retry: false });
  const pathsQ = useQuery({ queryKey: ['wiki', 'education', 'paths'], queryFn: () => educationApi.myPaths(), enabled: !!me, retry: false });
  const newsQ = useQuery({ queryKey: ['wiki', 'news', 'list'], queryFn: () => newsApi.list({ limit: 50 }), enabled: !!me, retry: false });
  const gamiQ = useQuery({ queryKey: ['wiki', 'gamification', 'mine'], queryFn: () => educationApi.gamificationMine(), enabled: !!me && isLearner, staleTime: 60_000, retry: false });
  const [articleOpen, setArticleOpen] = useState(false);

  const error = meQuery.error;
  const is401 = error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 401;
  if (is401) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Нужно войти"
        description={
          <div>
            Партнёр? <a href="https://crm.brasol.ru">Войдите в CRM</a>. Сотрудник? <Link to="/login">Войти по телефону</Link>.
          </div>
        }
      />
    );
  }
  if (meQuery.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  const counts = countsQ.data;
  const courses: LearnCourseListItem[] = coursesQ.data ?? [];
  const paths = pathsQ.data ?? [];
  const activePath = paths.find((p) => !p.completedAt) ?? null;
  const gami = gamiQ.data ?? null;
  const now = new Date();

  const pathExtra = audience === 'employee' && activePath ? pathDayLine(activePath) : null;
  const sub = [WEEKDAY_DATE(now), summaryLine(audience, summaryQ.data, pathExtra)].filter(Boolean).join(' · ');

  const todo = <TodoCard audience={audience} counts={counts} courses={courses} standards={standardsQ.data?.items ?? []} />;

  return (
    <div className="fk-hub-home">
      <div className="fk-hub-home-head">
        <div style={{ minWidth: 0 }}>
          <h1>
            {greetingOf(now.getHours())}, {firstNameOf(me)}
          </h1>
          <div className="fk-hub-home-sub">{sub}</div>
        </div>
        {audience === 'uk' && (
          <div className="fk-hub-home-actions">
            <Button variant="ghost" icon={<FileAddOutlined />} onClick={() => setArticleOpen(true)}>
              Новая статья
            </Button>
            <Button variant="primary" icon={<PlusOutlined />} to="/admin/courses/new">
              Новый курс
            </Button>
          </div>
        )}
        {isLearner && gami && <LearnerChips points={gami.points} streak={gami.streakDays} />}
      </div>

      {audience === 'uk' && !isOnboardingDone() && <OnboardingHero />}

      {audience === 'employee' && (
        <ContinueAndPath courses={courses} path={activePath} loading={coursesQ.isLoading} />
      )}

      <StatsRow audience={audience} counts={counts} courses={courses} path={activePath} gami={gami} />

      <div className="fk-hub-home-grid">
        <div className="fk-hub-home-colm">
          {audience === 'uk' && (
            <>
              <NetworkLearning />
              <AnswersQueue />
            </>
          )}
          {audience === 'partner' && (
            <>
              <MyPointsLearning title="Обучение моих точек" />
              <OpeningCard />
            </>
          )}
          {audience === 'manager' && <TeamLearning me={me} />}
          {/* Сотрудник (телефон-first): дела и вопрос дня — сразу под «Продолжить». */}
          {audience === 'employee' && (
            <>
              {todo}
              <QuestionOfDay compact />
            </>
          )}
          {audience !== 'employee' && activePath && <MyPathCard path={activePath} />}
          {audience === 'uk' ? <RecentUpdates /> : <Recommended />}
        </div>

        <div className="fk-hub-home-colm">
          {audience !== 'employee' && todo}
          {audience === 'manager' && <QuestionOfDay compact />}
          <ImportantNews items={newsQ.data?.items ?? []} loading={newsQ.isLoading} />
          {isLearner && <PointsWidget />}
          {audience === 'uk' && <PointsRating />}
          <SavedAndRecent />
          <AskAi />
        </div>
      </div>

      {articleOpen && <NewArticleModal open={articleOpen} onClose={() => setArticleOpen(false)} />}
    </div>
  );
}

// ── Шапка ученика: серия · очки · бейджи ──────────────────────────

function LearnerChips({ points, streak }: { points: number; streak: number }): JSX.Element {
  return (
    <div className="fk-hub-home-chips">
      <span className="fk-hub-home-chip">
        <FireOutlined style={{ color: 'var(--fk-orange)', fontSize: 20 }} />
        <span>
          <b>{pl(streak, ['день', 'дня', 'дней'])}</b>
          <small>серия</small>
        </span>
      </span>
      <span className="fk-hub-home-chip-sep" />
      <span className="fk-hub-home-chip">
        <StarOutlined style={{ color: 'var(--fk-orange)', fontSize: 20 }} />
        <span>
          <b>{points.toLocaleString('ru-RU')}</b>
          <small>очков · {levelOf(points)}</small>
        </span>
      </span>
      <span className="fk-hub-home-chip-sep" />
      <Link to="/points" className="fk-hub-card-link">
        Бейджи →
      </Link>
    </div>
  );
}

// ── Плитки по роли ─────────────────────────────────────────────────

function StatsRow({
  audience,
  counts,
  courses,
  path,
  gami,
}: {
  audience: Audience;
  counts: HubCountsDto | undefined;
  courses: CourseListItemDto[];
  path: MyPathDto | null;
  gami: { leaderboard: Array<{ rank: number; isMe: boolean }> } | null;
}): JSX.Element {
  const uk = audience === 'uk';
  const partner = audience === 'partner';
  const manager = audience === 'manager';
  const v = (n: number | null | undefined): ReactNode => (n == null ? '—' : n);

  // УК: курсы на согласовании (статус REVIEW).
  const lmsCourses = useQuery({ queryKey: ['wiki', 'lms', 'courses'], queryFn: () => lmsAdminApi.listCourses(), enabled: uk, retry: false, staleTime: 5 * 60_000 });
  // Партнёр: точки (просрочки сотрудников) и открытие.
  const pointsQ = useQuery({ queryKey: ['wiki', 'hub', 'my-points-learning'], queryFn: () => hubApi.myPointsLearning(), enabled: partner, retry: false, staleTime: 5 * 60_000 });
  const openingQ = useQuery({ queryKey: ['wiki', 'opening', 'me'], queryFn: () => openingApi.getMine(), enabled: partner, retry: false, staleTime: 5 * 60_000 });
  // Управляющий: команда и навыки, ждущие подтверждения наставником.
  const teamQ = useQuery({ queryKey: ['wiki', 'mentor', 'team', null], queryFn: () => educationApi.mentorTeam(null), enabled: manager, retry: false, staleTime: 5 * 60_000 });

  let stats: StatDef[];
  if (uk) {
    const inReview = (lmsCourses.data ?? []).filter((c) => c.status === 'REVIEW').length;
    stats = [
      { key: 'rev', value: v(counts?.reviews), label: 'ответов ждут проверки', icon: <AuditOutlined />, tone: 'orange', to: '/admin/reviews' },
      { key: 'fresh', value: v(counts?.freshness), label: 'статей просрочили проверку', icon: <ClockCircleOutlined />, tone: 'red', to: '/admin/freshness' },
      { key: 'q', value: v(counts?.questions), label: 'обращений и вопросов без ответа', icon: <InboxOutlined />, tone: 'blue', to: '/admin/questions' },
      { key: 'crs', value: lmsCourses.isLoading ? '—' : inReview, label: `${plural(inReview, ['курс', 'курса', 'курсов'])} на согласовании`, icon: <ReadOutlined />, tone: 'purple', to: '/admin/courses' },
    ];
  } else if (partner) {
    const overdueStaff = (pointsQ.data?.points ?? []).reduce((s, p) => s + p.overdueLearners, 0);
    const op = openingQ.data?.progress;
    stats = [
      { key: 'std', value: v(counts?.standards), label: 'стандартов на подтверждение', icon: <SafetyCertificateOutlined />, tone: 'orange', to: '/standards' },
      { key: 'late', value: pointsQ.isLoading ? '—' : overdueStaff, label: `${plural(overdueStaff, ['сотрудник', 'сотрудника', 'сотрудников'])} просрочили курс`, icon: <AlertOutlined />, tone: 'red' },
      op
        ? { key: 'open', value: `${op.progressPercent}%`, label: 'открытие точки', icon: <BankOutlined />, tone: 'blue', to: '/opening' }
        : { key: 'tasks', value: v(counts?.tasks), label: 'задач в работе', icon: <CheckSquareOutlined />, tone: 'blue', to: '/tasks' },
      { key: 'news', value: v(counts?.news), label: 'непрочитанные новости', icon: <SoundOutlined />, tone: 'purple', to: '/news' },
    ];
  } else if (manager) {
    const members = teamQ.data?.members ?? [];
    const waiting = members.reduce((s, m) => s + m.skills.filter((k) => k.level === 2).length, 0);
    stats = [
      { key: 'team', value: teamQ.isError ? '—' : members.length, label: 'в команде', icon: <TeamOutlined />, tone: 'blue', to: '/mentor' },
      { key: 'skills', value: teamQ.isError ? '—' : waiting, label: `${plural(waiting, ['навык ждёт', 'навыка ждут', 'навыков ждут'])} подтверждения`, icon: <CheckOutlined />, tone: 'orange', to: '/mentor' },
      { key: 'std', value: v(counts?.standards), label: 'стандартов на подтверждение', icon: <SafetyCertificateOutlined />, tone: 'green', to: '/standards' },
      { key: 'tasks', value: v(counts?.tasks), label: 'задач в работе', icon: <CheckSquareOutlined />, tone: 'purple', to: '/tasks' },
    ];
  } else {
    const cur = pickContinueCourse(courses);
    const myRank = gami?.leaderboard.find((r) => r.isMe)?.rank ?? null;
    stats = [
      cur
        ? { key: 'course', value: `${cur.progressPercent}%`, label: `курс «${cur.name}»`, icon: <ReadOutlined />, tone: 'orange', to: `/course/${cur.id}` }
        : { key: 'course', value: courses.filter((c) => c.isCompleted).length, label: 'курсов пройдено', icon: <ReadOutlined />, tone: 'orange', to: '/education' },
      { key: 'path', value: path ? `${path.doneSteps} / ${path.totalSteps}` : '—', label: 'шагов траектории', icon: <NodeIndexOutlined />, tone: 'blue', to: '/path' },
      { key: 'rank', value: myRank ?? '—', label: 'место в рейтинге точки', icon: <TrophyOutlined />, tone: 'green', to: '/points' },
      { key: 'std', value: v(counts?.standards), label: 'стандартов на подтверждение', icon: <SafetyCertificateOutlined />, tone: 'purple', to: '/standards' },
    ];
  }
  return (
    <div className="fk-hub-home-stats">
      {stats.map((s) => (
        <HomeStat key={s.key} s={s} />
      ))}
    </div>
  );
}

// ── Сотрудник: «Продолжить обучение» + траектория ─────────────────

function ContinueAndPath({ courses, path, loading }: { courses: LearnCourseListItem[]; path: MyPathDto | null; loading: boolean }): JSX.Element | null {
  const course = pickContinueCourse(courses) as LearnCourseListItem | null;
  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (!course && !path) return null;
  return (
    <div className="fk-hub-home-hero-row" style={!course || !path ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
      {course && <ContinueHero course={course} />}
      {path && <MyPathCard path={path} />}
    </div>
  );
}

function ContinueHero({ course }: { course: LearnCourseListItem }): JSX.Element {
  // Следующий урок — из списка курсов (W3, continueLesson*); старый бэк без полей — из карточки курса.
  const hasListFields = course.continueLessonId !== undefined;
  const detail = useQuery({ queryKey: ['wiki', 'education', 'course', course.id], queryFn: () => educationApi.getCourse(course.id), enabled: !hasListFields, retry: false, staleTime: 60_000 });
  const d = detail.data;
  const fromDetail = d?.continueLessonId ? d.modules.flatMap((m) => m.lessons).find((l) => l.id === d.continueLessonId) : null;
  const lessonId = hasListFields ? (course.continueLessonId ?? null) : (d?.continueLessonId ?? null);
  const lessonTitle = hasListFields ? course.continueLessonTitle : fromDetail?.title;
  const lessonMin = hasListFields ? course.continueLessonMinutes : fromDetail?.estimatedMinutes;
  const meta = [lessonTitle ? `Следующий урок: «${lessonTitle}»` : null, lessonMin ? `${lessonMin} мин` : null, course.dueAt ? `срок до ${fmtDay(course.dueAt)}` : null]
    .filter(Boolean)
    .join(' · ');
  const cv = coverVisual(course);
  return (
    <section className="fk-hub-hero" style={{ background: cv.heroBg }}>
      <CoverArt v={cv} />
      <CoverHeroText v={cv} />
      <span className="fk-hub-hero-kicker">Продолжить обучение</span>
      <h2>{course.name}</h2>
      {meta && <p>{meta}</p>}
      <div className="fk-hub-hero-progress">
        <Bar pct={course.progressPercent} />
        <b style={{ fontSize: 13 }}>{course.progressPercent}%</b>
      </div>
      <div className="fk-hub-hero-actions">
        <Button variant="primary" size="lg" icon={<PlayCircleOutlined />} to={lessonId ? `/lesson/${lessonId}` : `/course/${course.id}`}>
          {course.progressPercent > 0 ? 'Продолжить урок' : 'Начать курс'}
        </Button>
        <Button variant="ghost" size="lg" className="fk-hub-hero-ghost" to={`/course/${course.id}`}>
          Программа
        </Button>
      </div>
    </section>
  );
}

function MyPathCard({ path }: { path: MyPathDto }): JSX.Element {
  const currentIdx = path.steps.findIndex((s) => !s.done && !s.locked);
  return (
    <Card icon={<NodeIndexOutlined />} title="Моя траектория" link={{ to: `/path/${encodeURIComponent(path.assignmentId)}`, label: 'Открыть' }}>
      <div className="fk-hub-home-sub" style={{ marginTop: -8, marginBottom: 10 }}>
        {path.name}: <b>{path.doneSteps}</b> из {path.totalSteps} шагов
      </div>
      <div className="fk-hub-steps">
        {path.steps.slice(0, 6).map((s, i) => {
          const state = s.done ? 'done' : i === currentIdx ? 'current' : s.locked ? 'locked' : 'todo';
          const to = s.courseId ? `/course/${s.courseId}` : s.articleSlug ? `/article/${encodeURIComponent(s.articleSlug)}` : `/path/${encodeURIComponent(path.assignmentId)}`;
          return (
            <Link key={s.id} to={to} className="fk-hub-step" data-state={state}>
              <span className="fk-hub-step-dot">{state === 'done' ? <CheckOutlined /> : state === 'locked' ? <LockOutlined /> : state === 'current' ? <PlayCircleOutlined /> : i + 1}</span>
              <span className="fk-hub-ellipsis" style={{ minWidth: 0 }}>{s.title}</span>
              {s.dueAt && !s.done && <span className="fk-hub-step-due">до {fmtDay(s.dueAt)}</span>}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

// ── УК: обучение сети + очередь ответов ───────────────────────────

const ROLE_LABEL: Record<string, string> = {
  COOK: 'Повара',
  COURIER: 'Курьеры',
  CASHIER: 'Кассиры',
  OPERATOR: 'Операторы',
  MANAGER: 'Управляющие',
  PARTNER: 'Партнёры',
  ADMIN: 'УК',
};

function NetworkLearning(): JSX.Element {
  const overview = useQuery({ queryKey: ['wiki', 'admin', 'analytics', 'overview'], queryFn: () => wikiAdminApi.analyticsOverview(), retry: false, staleTime: 5 * 60_000 });
  const byRole = useQuery({ queryKey: ['wiki', 'admin', 'analytics', 'training-by-role'], queryFn: () => wikiAdminApi.analyticsTrainingByRole(), retry: false, staleTime: 5 * 60_000 });
  const o = overview.data;
  const kpis: Array<[string, string, string | null]> = o
    ? [
        ['Завершение обучения', `${o.eduPct}%`, null],
        ['Прочтение базы', `${o.readPct}%`, null],
        ['Соблюдение стандартов', `${o.compliancePct}%`, null],
        ['Активных', o.activeUsers.toLocaleString('ru-RU'), `из ${o.reach.toLocaleString('ru-RU')}`],
      ]
    : [];
  const rows = (byRole.data ?? []).slice(0, 6);
  return (
    <Card icon={<ReadOutlined />} title="Обучение сети" note="последние 30 дней" link={{ to: '/admin/analytics', label: 'Аналитика' }}>
      {overview.isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : o ? (
        <div className="fk-hub-kpis">
          {kpis.map(([label, value, hint]) => (
            <div key={label} className="fk-hub-kpi">
              <small>{label}</small>
              <b>
                {value}
                {hint && <span>{hint}</span>}
              </b>
            </div>
          ))}
        </div>
      ) : (
        <div className="fk-hub-muted" style={{ marginBottom: 12 }}>Показатели сети недоступны.</div>
      )}
      {rows.length > 0 && (
        <div className="fk-hub-bars">
          {rows.map((r, i) => (
            <Link key={r.role} to="/admin/analytics" className="fk-hub-bar-row" style={{ color: 'inherit', textDecoration: 'none' }}>
              <span>{ROLE_LABEL[r.role] ?? r.role}</span>
              <Bar pct={r.percent} color={AV_COLORS[i % AV_COLORS.length]} />
              <span className="fk-hub-bar-pct">{r.percent}%</span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

function reviewKind(r: ReviewQueueItem): { label: string; color: string } {
  const photo = r.questions.some((q) => q.kind === 'photo_upload' && !q.review);
  return photo ? { label: 'Фото-ответ', color: 'var(--fk-info)' } : { label: 'Открытый ответ', color: 'var(--fk-orange)' };
}

function AnswersQueue(): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'lms', 'reviews', 'pending'], queryFn: () => lmsAdminApi.listReviews('pending'), retry: false, staleTime: 60_000 });
  const items = q.data ?? [];
  return (
    <Card icon={<AuditOutlined />} title="Очередь на проверку" count={items.length} link={{ to: '/admin/reviews', label: 'Все ответы' }}>
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ответов на проверке нет" style={{ margin: '12px 0' }} />
      ) : (
        <div>
          {items.slice(0, 4).map((r) => {
            const k = reviewKind(r);
            return (
              <Link key={r.attemptId} to="/admin/reviews" className="fk-hub-row">
                <span className="fk-hub-av" style={{ background: avColor(r.user.fullName) }}>{initials(r.user.fullName)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="fk-hub-row-title" style={{ display: 'block' }}>
                    {r.user.fullName}
                    {r.user.roleName && <span style={{ fontWeight: 500, color: 'var(--fk-text-muted)' }}> · {r.user.roleName}</span>}
                  </span>
                  <span className="fk-hub-row-sub" style={{ display: 'block' }}>
                    {[r.course?.name, r.quiz.title].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: k.color }}>{k.label}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--fk-text-muted)' }}>{relTime(r.attemptedAt)}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** Рейтинг точек (УК): очки за месяц, топ-5. */
function PointsRating(): JSX.Element | null {
  const q = useQuery({ queryKey: ['wiki', 'an', 'points-rating'], queryFn: () => lmsAdminApi.pointsRating(), retry: false, staleTime: 5 * 60_000 });
  const rows = [...(q.data ?? [])].sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 5);
  if (!q.isLoading && rows.length === 0) return null;
  const max = rows[0]?.totalPoints || 1;
  return (
    <Card icon={<TrophyOutlined />} title="Рейтинг точек" link={{ to: '/admin/analytics', label: 'Аналитика' }}>
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        rows.map((r, i) => (
          <div key={r.tradingPointId} className="fk-hub-rank" data-first={i === 0 ? 'true' : 'false'}>
            <span>{i + 1}</span>
            <div style={{ minWidth: 0 }}>
              <div className="fk-hub-ellipsis" style={{ fontWeight: 600 }}>{r.city && !r.pointName.startsWith(r.city) ? `${r.city} · ${r.pointName}` : r.pointName}</div>
              <Bar pct={(r.totalPoints / max) * 100} />
            </div>
            <span style={{ fontWeight: 700, fontFamily: 'var(--fk-font-mono)', fontSize: 12 }}>{r.totalPoints.toLocaleString('ru-RU')}</span>
          </div>
        ))
      )}
    </Card>
  );
}

// ── Партнёр: обучение моих точек + открытие ───────────────────────

function MyPointsLearning({ title }: { title: string }): JSX.Element | null {
  const q = useQuery({ queryKey: ['wiki', 'hub', 'my-points-learning'], queryFn: () => hubApi.myPointsLearning(), retry: false, staleTime: 5 * 60_000 });
  const rows = q.data?.points ?? [];
  if (q.isError || (!q.isLoading && q.data?.scope === 'network')) return null;
  return (
    <Card icon={<ReadOutlined />} title={title}>
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Точек пока нет" style={{ margin: '12px 0' }} />
      ) : (
        rows.slice(0, 8).map((p) => {
          const color = p.pct === null ? 'var(--fk-border-strong)' : p.pct >= 80 ? 'var(--fk-success)' : p.pct >= 50 ? 'var(--fk-warning)' : 'var(--fk-error)';
          const late = p.overdue > 0 ? pl(p.overdue, ['просроченный курс', 'просроченных курса', 'просроченных курсов']) : 'без просрочек';
          return (
            <div key={p.id} className="fk-hub-point-row">
              <div className="fk-hub-point-row-head">
                <b className="fk-hub-ellipsis" style={{ minWidth: 0 }}>{p.name}</b>
                <b style={{ color, flexShrink: 0 }}>{p.pct === null ? '—' : `${p.pct}%`}</b>
              </div>
              <Bar pct={p.pct ?? 0} color={color} />
              <div className="fk-hub-row-sub" style={{ marginTop: 6, color: p.overdue > 0 ? 'var(--fk-error)' : undefined }}>
                {pl(p.learners, ['сотрудник', 'сотрудника', 'сотрудников'])} в HUB{p.staff > p.learners ? ` из ${p.staff}` : ''} · {p.assigned === 0 ? 'курсы не назначены' : late}
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}

function OpeningCard(): JSX.Element | null {
  const q = useQuery({ queryKey: ['wiki', 'opening', 'me'], queryFn: () => openingApi.getMine(), retry: false, staleTime: 5 * 60_000 });
  const p = q.data?.progress;
  if (!p) return null;
  const current = p.steps[p.currentStepIndex];
  const overdueStep = p.steps.find((s) => s.status === 'overdue');
  return (
    <Card icon={<BankOutlined />} title="Открытие точки" note={`${p.progressPercent}%`} link={{ to: '/opening', label: 'Все этапы' }}>
      <div className="fk-hub-stages">
        {p.steps.map((s, i) => {
          const done = s.status === 'completed';
          const cur = i === p.currentStepIndex;
          return (
            <div key={s.slug} className="fk-hub-stage" title={s.title}>
              <div style={{ background: done ? 'var(--fk-success)' : s.status === 'overdue' ? 'var(--fk-error)' : cur ? 'var(--fk-orange)' : 'var(--fk-border)' }} />
              <div style={{ color: cur ? 'var(--fk-orange)' : done ? 'var(--fk-text-2)' : 'var(--fk-text-muted)', fontWeight: cur ? 700 : 500 }}>{s.title}</div>
            </div>
          );
        })}
      </div>
      <div className="fk-hub-stage-note">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            Этап «{p.currentStepTitle}»{current ? ` · ${current.doneCount} из ${current.totalCount} шагов` : ''}
            {p.daysLeft != null ? ` · до открытия ${pl(p.daysLeft, ['день', 'дня', 'дней'])}` : ''}
          </div>
          {overdueStep ? (
            <div style={{ fontSize: 12.5, color: 'var(--fk-error)', marginTop: 2 }}>Просрочено: {overdueStep.title}</div>
          ) : p.overdueCount > 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--fk-error)', marginTop: 2 }}>Просрочено шагов: {p.overdueCount}</div>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" to="/opening">
          Открыть шаг
        </Button>
      </div>
    </Card>
  );
}

// ── Управляющий: команда точки ─────────────────────────────────────

function TeamLearning({ me }: { me: WikiMeDto | undefined }): JSX.Element | null {
  const q = useQuery({ queryKey: ['wiki', 'mentor', 'team', null], queryFn: () => educationApi.mentorTeam(null), retry: false, staleTime: 5 * 60_000 });
  // Управляющий iiko видит и процент обучения своей точки (my-points-learning).
  const pts = useQuery({ queryKey: ['wiki', 'hub', 'my-points-learning'], queryFn: () => hubApi.myPointsLearning(), enabled: !!me, retry: false, staleTime: 5 * 60_000 });
  if (q.isError) return <MyPointsLearning title="Обучение точек" />;
  const members = (q.data?.members ?? []).filter((m) => m.roleCode !== 'MANAGER');
  const point = pts.data?.points.length === 1 ? pts.data.points[0] : null;
  return (
    <Card icon={<TeamOutlined />} title="Команда точки · обучение" note={q.data?.point ?? undefined} link={{ to: '/mentor', label: 'Наставник' }}>
      {point && (
        <div className="fk-hub-point-row" style={{ paddingTop: 0 }}>
          <div className="fk-hub-point-row-head">
            <span className="fk-hub-row-sub">Назначенные курсы пройдены</span>
            <b>{point.pct === null ? '—' : `${point.pct}%`}</b>
          </div>
          <Bar pct={point.pct ?? 0} color={point.overdue > 0 ? 'var(--fk-warning)' : 'var(--fk-success)'} />
          {point.overdue > 0 && (
            <div className="fk-hub-row-sub" style={{ marginTop: 6, color: 'var(--fk-error)' }}>
              {pl(point.overdueLearners, ['сотрудник', 'сотрудника', 'сотрудников'])} с просрочкой
            </div>
          )}
        </div>
      )}
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : members.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="В команде пока никого" style={{ margin: '12px 0' }} />
      ) : (
        members.slice(0, 6).map((m) => {
          const total = m.skills.length;
          const confirmed = m.skills.filter((s) => s.level >= 3 && !s.expired).length;
          const expiring = m.skills.some((s) => s.expired);
          const pct = total ? Math.round((confirmed / total) * 100) : 0;
          return (
            <div key={m.ownerKey} className="fk-hub-row" style={{ cursor: 'default' }}>
              <span className="fk-hub-av" style={{ background: avColor(m.name) }}>{initials(m.name)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="fk-hub-row-title" style={{ display: 'block' }}>
                  {m.name} <span style={{ fontWeight: 500, color: 'var(--fk-text-muted)' }}>· {m.roleName}</span>
                </span>
                <span className="fk-hub-row-sub" style={{ display: 'block', color: expiring ? 'var(--fk-error)' : !m.hasAccount ? 'var(--fk-text-muted)' : undefined }}>
                  {!m.hasAccount ? 'нет доступа в HUB' : expiring ? 'есть просроченный навык' : total ? `навыки: ${confirmed} из ${total}` : 'навыков для роли нет'}
                </span>
              </span>
              <span style={{ width: 90, flexShrink: 0 }}>
                <Bar pct={pct} color={pct < 50 ? 'var(--fk-orange)' : 'var(--fk-success)'} />
              </span>
              <b style={{ width: 40, textAlign: 'right', fontSize: 13 }}>{pct}%</b>
            </div>
          );
        })
      )}
    </Card>
  );
}

// ── Что нужно сделать ──────────────────────────────────────────────

interface TodoItem {
  key: string;
  title: string;
  sub: string;
  to: string;
  icon: ReactNode;
  tone: Tone;
  bad?: boolean;
}

function TodoCard({
  audience,
  counts,
  courses,
  standards,
}: {
  audience: Audience;
  counts: HubCountsDto | undefined;
  courses: CourseListItemDto[];
  standards: Array<{ id: string; slug: string; title: string; isAcked: boolean }>;
}): JSX.Element {
  const navigate = useNavigate();
  const items: TodoItem[] = [];
  if (audience === 'uk') {
    if (counts?.reviews) items.push({ key: 'rev', title: `Проверить ${pl(counts.reviews, ['ответ', 'ответа', 'ответов'])} учеников`, sub: 'открытые ответы и фото', to: '/admin/reviews', icon: <AuditOutlined />, tone: 'orange' });
    if (counts?.freshness) items.push({ key: 'fresh', title: `Подтвердить актуальность: ${pl(counts.freshness, ['статья', 'статьи', 'статей'])}`, sub: 'просрочено или нет владельца', to: '/admin/freshness', icon: <ClockCircleOutlined />, tone: 'red', bad: true });
    if (counts?.questions) items.push({ key: 'q', title: `Разобрать ${pl(counts.questions, ['обращение', 'обращения', 'обращений'])} и вопросы`, sub: 'вопросы по базе и обращения из HUB', to: '/admin/questions', icon: <InboxOutlined />, tone: 'blue' });
  }
  // Ученику (и УК тоже) — стандарты к ознакомлению и курсы, как на /tasks.
  for (const st of standards.filter((s) => !s.isAcked)) {
    items.push({ key: `st-${st.id}`, title: `Подтвердить «${st.title}»`, sub: 'стандарт ждёт ознакомления', to: `/article/${encodeURIComponent(st.slug)}`, icon: <SafetyCertificateOutlined />, tone: 'blue' });
  }
  const open = courses.filter((c) => !c.isCompleted && (c.isAssigned || c.progressPercent > 0 || c.isRequired));
  for (const c of open) {
    const late = !!c.dueAt && new Date(c.dueAt).getTime() < Date.now();
    items.push({
      key: `c-${c.id}`,
      title: c.progressPercent > 0 ? `Продолжить «${c.name}»` : `Пройти «${c.name}»`,
      sub: c.dueAt ? (late ? `просрочено с ${fmtDay(c.dueAt)}` : `срок до ${fmtDay(c.dueAt)}`) : c.progressPercent > 0 ? `пройдено ${c.progressPercent}%` : c.isRequired ? 'обязательный курс' : 'назначен',
      to: `/course/${c.id}`,
      icon: <ReadOutlined />,
      tone: late ? 'red' : 'orange',
      bad: late,
    });
  }
  return (
    <Card icon={<CheckSquareOutlined />} title="Что нужно сделать" link={{ to: '/tasks', label: 'Все задачи' }}>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет активных задач" style={{ margin: '12px 0' }} />
      ) : (
        <div>
          {items.slice(0, 5).map((t) => {
            const [bg, fg] = TONE[t.tone];
            return (
              <button key={t.key} type="button" className="fk-hub-row" onClick={() => navigate(t.to)}>
                <span className="fk-hub-row-ic" style={{ background: bg, color: fg }}>{t.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="fk-hub-row-title" style={{ display: 'block', whiteSpace: 'normal' }}>{t.title}</span>
                  <span className="fk-hub-row-sub" style={{ display: 'block', color: t.bad ? 'var(--fk-error)' : undefined }}>{t.sub}</span>
                </span>
                <RightOutlined style={{ color: 'var(--fk-text-muted)', fontSize: 11 }} />
              </button>
            );
          })}
          {items.length > 5 && <div className="fk-hub-row-sub" style={{ paddingTop: 8 }}>и ещё {items.length - 5} — в «Мои задачи»</div>}
        </div>
      )}
    </Card>
  );
}

// ── Обновления базы / рекомендации ────────────────────────────────

function RecentUpdates(): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'articles', 'recent-home'], queryFn: () => wikiApi.listArticles({ status: 'PUBLISHED', limit: 5 }), retry: false });
  const items = q.data?.items ?? [];
  return (
    <Card icon={<FileTextOutlined />} title="Последние обновления базы" link={{ to: '/knowledge', label: 'База знаний' }}>
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет статей" style={{ margin: '12px 0' }} />
      ) : (
        items.map((a) => (
          <Link key={a.id} to={`/article/${encodeURIComponent(a.slug)}`} className="fk-hub-row">
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="fk-hub-row-title" style={{ display: 'block' }}>{a.title}</span>
              <span className="fk-hub-row-sub" style={{ display: 'block' }}>
                {[a.categoryName, a.readingMinutes ? `${a.readingMinutes} мин` : null].filter(Boolean).join(' · ')}
              </span>
            </span>
            {a.publishedAt && <span className="fk-hub-row-sub" style={{ flexShrink: 0 }}>{relTime(a.publishedAt)}</span>}
          </Link>
        ))
      )}
    </Card>
  );
}

/** «Рекомендовано вам»: обязательное непрочитанное → для роли → популярное → новое. */
function Recommended(): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'recommended'], queryFn: () => wikiApi.recommended(6), staleTime: 120_000, retry: false });
  const items = q.data ?? [];
  const tone: Record<string, string> = { ack: 'var(--fk-error)', role: 'var(--fk-orange)', popular: 'var(--fk-info)', new: 'var(--fk-text-muted)' };
  return (
    <Card icon={<FileTextOutlined />} title="Рекомендовано вам" link={{ to: '/knowledge', label: 'База знаний' }}>
      {q.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Вы прочитали всё важное" style={{ margin: '12px 0' }} />
      ) : (
        items.map((a) => (
          <Link key={a.id} to={`/article/${encodeURIComponent(a.slug)}`} className="fk-hub-row">
            <span style={{ width: 22, textAlign: 'center', flexShrink: 0 }}>{a.icon ?? <FileTextOutlined style={{ color: 'var(--fk-text-muted)' }} />}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="fk-hub-row-title" style={{ display: 'block' }}>{a.title}</span>
              <span className="fk-hub-row-sub" style={{ display: 'block', color: tone[a.reasonKind] }}>
                {a.reason}
                {a.categoryName ? ` · ${a.categoryName}` : ''}
              </span>
            </span>
          </Link>
        ))
      )}
    </Card>
  );
}

// ── Важные новости ─────────────────────────────────────────────────

const PRIORITY_TAG: Record<string, [string, string, string]> = {
  IMPORTANT: ['Важно', 'var(--fk-error)', 'var(--fk-error-soft)'],
  REQUIRES_ACTION: ['Требует действия', 'var(--fk-orange)', 'var(--fk-orange-soft)'],
};

function ImportantNews({ items, loading }: { items: NewsItemDto[]; loading: boolean }): JSX.Element {
  const [open, setOpen] = useState<NewsItemDto | null>(null);
  const news = useMemo(() => {
    const important = items.filter((n) => n.isPinned || n.priority === 'IMPORTANT' || n.priority === 'REQUIRES_ACTION');
    const rest = items.filter((n) => !important.includes(n));
    return [...important, ...rest].slice(0, 3);
  }, [items]);
  return (
    <Card icon={<SoundOutlined />} title="Важные новости" link={{ to: '/news', label: 'Все новости' }}>
      {loading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : news.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Пока нет новостей" style={{ margin: '12px 0' }} />
      ) : (
        news.map((n) => {
          const tag = n.priority ? PRIORITY_TAG[n.priority] : undefined;
          return (
            <button key={n.id} type="button" className="fk-hub-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 3 }} onClick={() => setOpen(n)}>
              <span style={{ fontSize: 11.5, color: 'var(--fk-text-muted)' }}>
                {tag && (
                  <span className="fk-hub-news-tag" style={{ color: tag[1], background: tag[2] }}>
                    {tag[0]}
                  </span>
                )}
                {fmtDay(n.publishedAt ?? n.createdAt)}
              </span>
              <span className="fk-hub-row-title" style={{ whiteSpace: 'normal' }}>{n.title}</span>
            </button>
          );
        })
      )}
      <NewsModal item={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

// ── Избранное и недавние ───────────────────────────────────────────

function SavedAndRecent(): JSX.Element | null {
  const [tab, setTab] = useState<'fav' | 'recent'>('fav');
  const fav = useQuery({ queryKey: ['wiki', 'favorites'], queryFn: () => wikiApi.listFavorites(), staleTime: 30_000, retry: false });
  const recent = useMemo(() => readRecent(), []);
  const favItems = fav.data ?? [];
  if (!fav.isLoading && favItems.length === 0 && recent.length === 0) return null;
  const list = tab === 'fav' ? favItems.slice(0, 6).map((a) => ({ key: a.id, slug: a.slug, title: a.title, icon: a.icon })) : recent.slice(0, 6).map((a) => ({ key: a.slug, slug: a.slug, title: a.title, icon: a.icon }));
  return (
    <section className="fk-hub-card">
      <div className="fk-hub-card-head">
        <div className="fk-hub-card-title" style={{ gap: 14 }}>
          {(['fav', 'recent'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className="fk-hub-linkbtn"
              onClick={() => setTab(k)}
              style={{ fontSize: 15, fontWeight: 700, color: tab === k ? 'var(--fk-text)' : 'var(--fk-text-muted)' }}
              aria-pressed={tab === k}
            >
              {k === 'fav' ? <StarOutlined /> : <ClockCircleOutlined />} {k === 'fav' ? 'Избранное' : 'Недавние'}
            </button>
          ))}
        </div>
      </div>
      {list.length === 0 ? (
        <div className="fk-hub-row-sub" style={{ whiteSpace: 'normal' }}>
          {tab === 'fav' ? 'Нажмите ★ в шапке статьи — она появится здесь.' : 'Здесь будут статьи, которые вы открывали.'}
        </div>
      ) : (
        list.map((a) => (
          <Link key={a.key} to={`/article/${encodeURIComponent(a.slug)}`} className="fk-hub-row" style={{ padding: '8px 0' }}>
            <span style={{ width: 20, textAlign: 'center', flexShrink: 0 }}>{a.icon || <FileTextOutlined style={{ color: 'var(--fk-text-muted)' }} />}</span>
            <span className="fk-hub-row-title" style={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{a.title}</span>
          </Link>
        ))
      )}
    </section>
  );
}

// ── Спросите AI ────────────────────────────────────────────────────

function AskAi(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <section className="fk-hub-card fk-hub-ai">
      <img src="/brand/illustrations/nigiri.png" alt="" className="fk-hub-ai-img" />
      <div className="fk-hub-card-title">
        <ThunderboltOutlined />
        <h2>Спросите AI</h2>
      </div>
      <div className="fk-hub-row-sub" style={{ marginTop: 4, whiteSpace: 'normal' }}>где найти инструкцию, стандарт или документ?</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          navigate(`/ai?q=${encodeURIComponent(q.trim() || 'Как закрыть смену в IIKO?')}`);
        }}
      >
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Как закрыть смену в IIKO?" aria-label="Вопрос AI-помощнику" />
        <button type="submit" aria-label="Спросить">
          <SendOutlined />
        </button>
      </form>
    </section>
  );
}
